<template>
	<div class="cell">
		<div class="upload-field" :class="[type, { 'upload-field--error': fieldHasError }]">
			<template v-if="hasSelectedFormat">
				<div v-show="separateCover || id === 'cover'" class="h3">{{ name }}</div>
				<p v-if="id === 'content' !== null" class="mb-s">
					<span v-if="pageCount && pageCount > 1 && id !== 'cover'">
						{{ pageCount }} pagina&apos;s,
					</span>
					<span v-if="id === 'cover'">
						<spine inline />
					</span>
					{{ selectedFormat.productSize.name }}
				</p>
			</template>
			<div class="upload-field--wrapper" v-show="!hasInput">
				<template v-if="!coverUpload.bool">
					<file-upload
						class="file-upload-field"
						:post-action="postUrl"
						extensions="pdf"
						accept="application/pdf"
						:multiple="false"
						:drop="true"
						:drop-directory="false"
						:size="1024 * 1024 * 1024"
						v-model="files"
						@input-filter="inputFilter"
						@input-file="inputFile"
						@input="handleInput"
						:input-id="'configurator-upload-' + id"
						ref="upload">
						<icons upload />
						<span class="file-upload-field__copy">
							{{ uploadText }}
						</span>
						<span class="file-upload-field__copy--browse">Browse</span>
					</file-upload>
				</template>
				<template v-else-if="coverUpload.bool">
					<file-upload
						class="file-upload-field"
						:post-action="postUrl"
						extensions="pdf"
						accept="application/pdf"
						:multiple="false"
						:drop="true"
						:drop-directory="false"
						:size="1024 * 1024 * 1024"
						v-model="files"
						@input-filter="inputFilter"
						@input-file="inputFile"
						@input="handleInput"
						:input-id="'configurator-upload-' + id"
						ref="upload"
						v-if="coverUpload.valid">
						<icons upload />
						<span class="file-upload-field__copy">
							{{ uploadText }}
						</span>
						<span class="file-upload-field__copy--browse">Browse</span>
					</file-upload>
					<div
						v-else-if="!coverUpload.valid"
						class="file-upload-field file-upload-field--disabled">
						{{ uploadText }}
					</div>
				</template>
			</div>
			<ul class="files" v-show="hasInput">
				<li
					v-for="file in files" :key="file.id"
					class="file"
					@click.prevent="uploadNewFile">
					<span class="file-upload-field file-upload-field--valid">
						<icons file />
						{{ customErrorMessage }}
						<span class="file-upload-field__progress" v-if="progress < 100">
							<span class="file-upload-field__progress-animation" />
							<span class="file-upload-field__copy">uploading</span>
						</span>
						<span v-else-if="file.error" class="error-message">{{ errorMessage }}</span>
						<span v-else-if="fieldHasError && selectedFormat.id" class="file-upload-field__copy">
							Het bestand voldoet niet aan de specificaties
							<span @click.stop>
								<modal
									title="Aanleverspecificaties"
									button-style="inline"
									holder-class="file-upload-field__inline-copy-container"
									modal-style="styled"
									button-text="Bekijk de aanleverspecificaties">
									<delivery-specification :format="selectedFormat" overview />
								</modal>
							</span>
						</span>
						<span v-else-if="file.success && !customErrorMessage" class="file-upload-field__copy">
							{{ file.name }}
						</span>
						<span class="file-upload-field__copy--browse">Browse</span>
					</span>
				</li>
			</ul>
		</div>
	</div>
</template>

<script>
import FileUpload from "vue-upload-component";
import icons from "@/components/element/icons";
import spine from "@/components/element/spine";
import modal from "@/components/element/modal";
import deliverySpecification from "@/components/information/delivery-specification";
import { EventBus } from "@/store/index.js";

export default {
	name: "UploadField",
	components: {
		FileUpload,
		icons,
		spine,
		modal,
		deliverySpecification
	},
	props: {
		separateCover: {
			type: Boolean,
			default: false
		},
		type: {
			type: String,
			default: null
		},
		id: {
			type: String,
			default: null
		},
		name: {
			type: String,
			default: null
		},
		isLast: {
			type: Boolean,
			default: false
		},
		position: {
			type: Object,
			required: true
		}
	},
	data () {
		return {
			hasInput: false,
			files: [],
			fieldHasError: false
		};
	},
	computed: {
		file () {
			return this.files[0];
		},
		progress () {
			return Math.round(this.file.progress);
		},
		success () {
			return this.file.success;
		},
		postUrl () {
			if (!this.uploadSessionGuid) {
				return null;
			}
			if (this.type === "content") {
				return window.PDF_API_HOST
					+ "upload-configurator/?contentUploadPublicId=" + this.uploadSessionGuid
					+ "&productFrontId=" + this.productFrontId
					+ "&specificationId=" + this.getProductSpecificationId
					+ "&type=Content"
					+ "&ShopId=" + window.SHOP_ID;
			} else {
				return window.PDF_API_HOST
					+ "upload-configurator/?contentUploadPublicId=" + this.uploadSessionGuid
					+ "&productFrontId=" + this.productFrontId
					+ (this.$store.getters.getFile.contentIsUploaded ? "&specificationId=" + this.$store.getters.getFile.content.specificationIds[0] : "")
					+ "&type=Cover"
					+ "&ShopId=" + window.SHOP_ID;
			}
		},
		uploadSessionGuid () {
			return this.$store.getters.getUploadSessionGuid;
		},
		productFrontId () {
			const selectedProduct = this.$store.getters.getSelectedProduct;
			return selectedProduct ? selectedProduct.id : this.$store.state.baseProps.productFrontId[0];
		},
		getProductSpecificationId () {
			return this.$store.getters.getSelectedFormat.id;
		},
		errorMessage () {
			const errorMessage = (error) => ({
				"size": "Maximum bestandsgrootte overschreden.",
				"extension": "Onjuist bestandsformaat.",
				"timeout": "De verbinding werd verbroken.",
				"abort": "Geannuleerd.",
				"network": "Er is iets misgegaan tijdens het uploaden van het bestand.",
				"server": "Er is iets misgegaan tijdens het uploaden van het bestand.",
				"denied": "Bestand geweigerd.",
				"default": "Er is iets mis gegaan."
			})[error];

			return errorMessage(this.file.error) || errorMessage("default");
		},
		customErrorMessage () {
			const errorMessages = this.$store.getters.getCustomErrorMessages;
			return errorMessages[this.type];
		},
		uploadText () {
			return this.coverUpload.bool
				? this.coverUpload.valid
					? "Sleep uw bestand hierheen of selecteer een bestand"
					: "Upload binnenwerk eerst"
				: "Sleep uw bestand hierheen of selecteer een bestand";
		},
		coverUpload () {
			return {
				valid: this.$store.getters.getFile.contentIsUploaded && this.$store.getters.getFile.customErrorMessages.content === null,
				bool: this.separateCover && this.type === "cover"
			};
		},
		uploadsAreComplete () {
			return this.$store.getters.uploadsAreComplete;
		},
		selectedFormat () {
			return this.$store.getters.getSelectedFormat;
		},
		hasSelectedFormat () {
			return this.selectedFormat.id && this.selectedFormat.id !== null;
		},
		pageCount () {
			return this.$store.getters.getPageCount;
		},
		productWidth () {
			return this.selectedFormat.productSize.widthInMm;
		},
		productHeight () {
			return this.selectedFormat.productSize.heightInMm;
		}
	},
	methods: {
		inputFilter (newFile, oldFile, prevent) {
			if (newFile && !oldFile) {
				// Before adding a file
				// Filter system files or hide files
				if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
					return prevent();
				}
				// Filter php html js file
				if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
					return prevent();
				}
			}
		},
		inputFile (newFile, oldFile) {
			if (newFile && !oldFile) {
				// add
				if (!this.$refs.upload.active) {
					this.$refs.upload.active = true;
				}
				this.hasInput = true;
			}
			if (newFile && oldFile) {
				if (newFile.success !== oldFile.success) {
					// success handler
					this.handleSuccess(newFile.response);
				}
			}
		},
		handleInput () {
			this.fieldHasError = false;
		},
		fileIsValid (response) {
			this.fieldHasError = false;

			if ((!this.hasSelectedFormat)
				|| (this.id === "cover" && this.customErrorMessage === null)
				|| ((this.pageCount === response.pageCount) && (response.uploadSize.width === this.productWidth) && (response.uploadSize.height === this.productHeight))) {
				return true;
			}

			this.fieldHasError = true;
			return false;
		},
		handleSuccess (response) {
			response.mainPreviewUrl = response.mainPreviewUrl
				? response.mainPreviewUrl + "?v=" + new Date().getTime().toString()
				: null;
			response.type = this.type;
			response.message = response.errors && response.errors.length > 0
				? this.aggregatePageMessages(response.errors).join(" ")
				: response.errorMessage;

			if (response.uploadSize) {
				response.uploadSize = {
					width: Math.round(response.uploadSize.width),
					height: Math.round(response.uploadSize.height)
				};
			}

			this.$store.commit("SET_FILE_UPLOAD", response);
		},
		aggregatePageMessages (messages) {
			const result = [];
			let current = null;

			const flush = () => {
				if (!current) return;
				const word = current.capitalized ? "Pagina " : "pagina ";
				const pageRef = current.startPage === current.endPage
					? word + current.startPage
					: word + current.startPage + " t/m " + current.endPage;
				result.push(current.template.replace("___PAGE_REF___", pageRef));
				current = null;
			};

			for (let i = 0; i < messages.length; i++) {
				const msg = messages[i];
				const match = msg.match(/pagina (\d+)/i);
				if (match) {
					const pageNum = parseInt(match[1], 10);
					const capitalized = match[0][0] === "P";
					const template = msg.replace(/pagina \d+/i, "___PAGE_REF___");
					if (current && current.template === template && current.capitalized === capitalized && pageNum === current.endPage + 1) {
						current.endPage = pageNum;
					} else {
						flush();
						current = { template: template, startPage: pageNum, endPage: pageNum, capitalized: capitalized };
					}
				} else {
					flush();
					result.push(msg);
				}
			}
			flush();
			return result;
		},
		uploadNewFile () {
			if (!this.isLast) {
				this.$store.commit("SET_STEP_INVALID");
			}
			this.$store.commit("RESET_FORMAT_SUGGESTIONS");
			this.$store.commit("RESET_FORMAT_MATCHES");

			this.$store.commit("RESET_CUSTOM_ERROR_MESSAGE", this.type);
			this.$store.commit("RESET_FILE_UPLOAD", this.type);

			this.$store.commit("RESET_MATCHED_CUSTOMSIZE_FORMAT_SUGGESTION");

			if (this.type === "content" && this.separateCover) {
				this.$store.commit("RESET_CUSTOM_ERROR_MESSAGE", "cover");
				this.$store.commit("RESET_FILE_UPLOAD", "cover");
				EventBus.$emit("resetUploadInput");
			}

			if (!this.isLast) {
				this.$store.commit("CONFIGURATOR_STEP_AVAILABLE");
			}
			this.$refs.upload.active = false;
			this.hasInput = false;
		}
	},
	watch: {
		customErrorMessage (newValue, oldValue) {
			if (newValue !== null) {
				this.fieldHasError = true;
			}
		}
	},
	mounted () {
		EventBus.$on("resetUploadInput", () => {
			if (this.files.length > 0) {
				this.files[0].success = false;
			}

			this.hasInput = false;
		});
	}
};
</script>

<style lang="scss" scope="global">
.upload-field {
	display: flex;
	flex-direction: column;
	height: 100%;

	&--wrapper {
		display: flex;
		height: 100%;
	}
}

.file-upload-field {
	display: flex !important;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	min-height: 125px;
	width: 100%;
	padding: 24px;
	border: 2px dashed var(--cfg-primary-faint);
	border-radius: 8px;
	flex: 1;

	color: #26272B;
	user-select: none;
	font-weight: bold;
	font-size: 1.4rem;
	text-align: center;
	line-height: normal;

	cursor: pointer;
	transition: background 0.15s ease-out;

	&:hover {
		background: var(--cfg-primary-tint-alt);
	}

	&--disabled {
		cursor: not-allowed;
		border: 2px solid #F9FAFB;
		background: #F9FAFB;

		&:hover {
			background: #F9FAFB;
		}
	}

	&--valid {
		border: 2px solid var(--cfg-primary);
		background: var(--cfg-primary-tint);

		.configurator__icon-container {
			background: #E0EAFF;
		}
	}

	.configurator__icon-container {
		height: 41px;

		svg {
			height: 25px;
		}
	}
}

.upload-field--error .file-upload-field--valid {
	border: 2px dashed#F04438;
	background: var(--cfg-error-light-bg);

	.configurator__icon-container {
		background: var(--cfg-error-light-bg);

		svg path {
			stroke: #F04438
		}
	}
}

.upload-field__spine:empty {
	display: none;
}

.file-upload-field__progress {
	display: flex;
	align-items: center;
	gap: 6px;
}

.file-upload-field__progress-animation {
	display: inline-grid;
	width: 40px;
	aspect-ratio: 1;
	border: 2px solid #0000;
	border-radius: 50%;
	border-right-color: #3498db;
	animation: spinning 1s infinite linear;

	&:after,
	&:before {
		content: "";
		grid-area: 1/1;
		margin: 8px;
		border: inherit;
		border-right-color: var(--cfg-warning-alt);
		border-radius: 50%;
		animation: spinning 2s infinite;
	}

	&:after {
		margin: 3px;
		border-right-color: var(--cfg-warning);
		animation-duration: 3s;
	}
}

@keyframes spinning {
	100% {
		transform: rotate(1turn);
	}
}

.file-upload-field__copy {
	font-size: 13px;
	font-weight: 400;
	line-height: 1.5;

	&--browse {
		font-size: 15px;
		font-weight: 600;
		line-height: 1.4;
		letter-spacing: 0.2px;
		color: var(--cfg-primary);
	}
}

.file-upload-field__inline-copy-container {
	width: 100%;
	text-align: center;
	text-decoration: underline;
}

.files {
	margin: 0;
	padding: 0;
	text-align: left;
}

.document-icon {
	width: 80px;
	height: 80px;
}

.progress-bar {
	width: 100%;
	height: 6px;
	background: #eee;
	position: relative;
}

.progress-filler {
	content: "";
	position: absolute;
	height: 100%;
	width: 0%;
	background: var(--cfg-error);
	transition: all 0.5s ease;
}

.progress-percentage {
	font-weight: 900;
	color: var(--cfg-error);
	font-size: 2.5rem;
	position: relative;
	z-index: 1;
}

.new-upload-button {
	font-size: 1.6rem;
	color: #404040;
	cursor: pointer;
	font-weight: 300;
}
</style>



// WEBPACK FOOTER //
// src/components/uploader/upload-field.vue